import { useCallback } from 'react';
import { merge, noop, values } from 'lodash';
import {
    SCHEDULE,
    SERVICE_TESTS_API_BASE,
    SERVICE_TESTS_UPDATE_DOC_STATUS,
    TESTS_LIST_UPDATE_ENDPOINT,
    useDataTableActionService,
    useFormDataTableActionWrapper,
    usePostDataTableActionHandler,
} from '../../../../services/FormDataTableActionService';
import { useInfrastructureDeleteError } from './UseInfrastructureDeleteError';
import { resolveErrorHandler } from '../InfrastructureUtils';

/**
 * Build action parameters by merging table state, data source params, and custom params
 * @param {Object} filterState - search type info
 * @param {Object} tableState - Current table state (search, filters, etc.)
 * @param {Object} dataSourceParams - Data source parameters
 * @param {Object} customParams - Custom parameters for specific actions
 * @returns {Object} Merged parameters object
 */
export const buildActionParams = (filterState, tableState, dataSourceParams, customParams = {}) => {
    const filterParams = values(filterState?.parameters || {});

    return merge(
        {
            search: tableState?.search || '',
        },
        ...filterParams,
        dataSourceParams,
        customParams
    );
};

export const buildServiceTestsPayload = (tableSelection, updateDoc) => {
    const { inverted, selection, selectionLength } = tableSelection;
    let selectionParams;
    if (inverted) {
        selectionParams = {
            count: selectionLength,
            selection,
            inverted,
            valid: true,
        };
    } else {
        selectionParams = {
            count: selection.length,
            selection,
            inverted,
            valid: true,
        };
    }

    return {
        updateDoc,
        selectionParams,
    };
};

/**
 * Custom hook for handling infrastructure list actions (Enable, Disable, Delete, Rescan, etc.)
 * Provides a unified interface for executing data table actions with proper error handling
 *
 * @param {Object} config - Configuration object
 * @param {Object} config.dataSource - Data source configuration with params
 * @param {Object} config.actionUrls - Map of action names to API URLs
 * @param {Function} config.refresher - Function to trigger table refresh after action
 * @param {Function} config.handleSuccessfulAction - Optional success handler callback
 * @param {Function} config.customActionHandler - Optional custom action handler for specific actions
 * @param {Object} config.selectionData - Reference to current table selection and rows
 * @returns {Object} Action handlers and state
 * @returns {boolean} returns.dataTableActioning - Whether an action is currently in progress
 * @returns {Function} returns.handleConfirmedDataTableAction - Handler for confirmed actions
 *
 * @example
 * const { dataTableActioning, handleConfirmedDataTableAction } = useInfrastructureListActions({
 *   dataSource: { params: { type: 'device' } },
 *   actionUrls: { Delete: '/api/delete', Enable: '/api/enable' },
 *   refresher: () => reloadTable(),
 *   selectionData: selectionRef
 * });
 */
export function useInfrastructureListActions({
    dataSource,
    actionUrls,
    refresher,
    handleSuccessfulAction,
    customActionHandler = noop,
    selectionData,
}) {
    const formDataTableActionService = useDataTableActionService();
    const handleDeleteError = useInfrastructureDeleteError();
    const { dataTableActioning, dataTableActionWrapper, handleSuccessfulDataTableAction } =
        useFormDataTableActionWrapper();

    // Wrap customActionHandler to inject selectionData context
    const wrappedCustomActionHandler = useCallback(
        (response) => customActionHandler(response, selectionData),
        [customActionHandler, selectionData]
    );

    const postDataTableActionHandlerFactory = usePostDataTableActionHandler(
        handleSuccessfulAction || handleSuccessfulDataTableAction,
        refresher,
        wrappedCustomActionHandler
    );

    /**
     * Handle confirmed data table action
     * @param {Event} event - Click event from action button
     * @param {string} action - Action name (ENABLE, DISABLE, DELETE, etc.)
     * @param {Object} filterState - search type info
     * @param {Object} tableSelection - Current table selection state
     * @param {Object} tableState - Current table state (search, filters, pagination)
     * @param {boolean} needAlert - Whether to show success alert after action completes
     * @param {Object} customParams - Optional custom parameters to merge with action request
     * @returns {Promise} Promise that resolves when action completes
     */
    const handleConfirmedDataTableAction = useCallback(
        (
            event,
            action,
            actionPayloadParams,
            filterState,
            tableSelection,
            tableState,
            needAlert = false,
            customParams = {}
        ) => {
            const actionUrl = actionUrls[action];
            const buildParams = buildActionParams(filterState, tableState, dataSource.params, customParams);

            // For Service Tests action, with below logic, for others not change
            let newTableSelection;
            let params;
            if (SERVICE_TESTS_API_BASE === actionUrl || TESTS_LIST_UPDATE_ENDPOINT === actionUrl) {
                let updateDoc;
                if (SCHEDULE === action) {
                    updateDoc = actionPayloadParams;
                } else {
                    updateDoc = SERVICE_TESTS_UPDATE_DOC_STATUS[action];
                }
                const { testTypes } = tableSelection;

                newTableSelection = buildServiceTestsPayload(tableSelection, updateDoc);
                const { query, ...withoutQueryParams } = buildParams;
                params = { ...withoutQueryParams, testTypes, rowLimit: 101 };
            } else {
                newTableSelection = tableSelection;
                params = buildParams;
            }

            const postDataTableActionHandler = postDataTableActionHandlerFactory(action, needAlert);

            // Remove focus from button to prevent keyboard re-trigger
            event?.currentTarget.blur();

            const errorHandler = resolveErrorHandler(actionUrl, handleDeleteError);

            return dataTableActionWrapper({
                dataTableActionFunction: () => {
                    return formDataTableActionService.dataTableActionListSelection(
                        actionUrl,
                        params,
                        newTableSelection,
                        errorHandler
                    );
                },
                refresherFunc: refresher,
                postDataTableActionHandler,
                action,
                needAlert,
            })();
        },
        [
            dataSource.params,
            actionUrls,
            dataTableActionWrapper,
            formDataTableActionService,
            handleDeleteError,
            postDataTableActionHandlerFactory,
            refresher,
        ]
    );

    return {
        dataTableActioning,
        handleConfirmedDataTableAction,
    };
}
